import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import Loading from '../../../components/loading/Loading';
import Toast from '../../../components/support/Toast';
import CompanyStatusBadge from './components/CompanyStatusBadge';
import CompanyTestChip from './components/CompanyTestChip';
import CompanyClosureChip from './components/CompanyClosureChip';
import CompanyClosureCard from './components/CompanyClosureCard';
import CompanySetupCard from './components/CompanySetupCard';
import CompanyOnboardingCard from './components/CompanyOnboardingCard';
import CompanyTutorialsCard from './components/CompanyTutorialsCard';
import StartCompanyClosureDialog from './components/StartCompanyClosureDialog';
import SendCompanyMessageDialog, { hasCompanyMessageContacts, describeDeliveries, NO_CONTACTS_HINT } from './components/SendCompanyMessageDialog';
import CompanyInfoCard from './components/CompanyInfoCard';
import CompanySignupOriginCard from './components/CompanySignupOriginCard';
import CompanyOutreachOutcomeCard from './components/CompanyOutreachOutcomeCard';
import CompanyReferralCard from './components/CompanyReferralCard';
import CompanyDemoDataCard from './components/CompanyDemoDataCard';
import CompanyUsersTable from './components/CompanyUsersTable';
import DeleteCompanyDialog, { DELETE_LOCKED_HINT } from './components/DeleteCompanyDialog';
import MarkCompanyTestDialog, { MARK_TEST_HINT, COMPANY_ZERO_TEST_HINT, canMarkCompanyTest } from './components/MarkCompanyTestDialog';
import ReopenCompanyTrialDialog, { REOPEN_TRIAL_DAYS, reopenTrialBlockedReason } from './components/ReopenCompanyTrialDialog';
import { GetAdminCompanyAPI, SetAdminCompanyTestAPI, StartAdminCompanyClosureAPI, ReopenAdminCompanyTrialAPI } from '../../../api/AdminAPI';
import { formatDate } from '../../../utils/Utils';
import { formatPhone } from '../../../utils/phoneUtils';

/**
 * Formata uma data 'Y-m-d H:i:s' usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

const SUBSCRIPTION_STATUS_LABEL = {
  active: 'Ativa',
  inactive: 'Inativa',
};

const WHATSAPP_STATUS_LABEL = {
  connected: 'Conectado',
  not_configured: 'Não configurado',
};

/**
 * AdminCompanyDetails - Detalhes de uma empresa (Admin Geral)
 *
 * Responsabilidades:
 * - Carrega os dados da empresa via GetAdminCompanyAPI(id)
 * - Renderiza cabeçalho, cards de uso/assinatura/whatsapp/fiscal e tabela de usuários
 * - Orquestra ações: marcar/desmarcar teste, liberar novo período de teste, iniciar encerramento,
 *   excluir empresa e todos os dados
 *
 * Exclusão liberada para conta de teste ou com a carência do encerramento vencida;
 * os demais pré-requisitos (arquivo de segurança, assinatura, WhatsApp) vêm do
 * servidor como bloqueios no DeleteCompanyDialog.
 */
function AdminCompanyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [onboardingEmails, setOnboardingEmails] = useState([]);
  const [referral, setReferral] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [markTestDialogOpen, setMarkTestDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [markingTest, setMarkingTest] = useState(false);
  const [startClosureOpen, setStartClosureOpen] = useState(false);
  const [startingClosure, setStartingClosure] = useState(false);
  const [reopenTrialOpen, setReopenTrialOpen] = useState(false);
  const [reopeningTrial, setReopeningTrial] = useState(false);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // silent: recarrega sem trocar a página pelo Loading (ex.: arquivo de segurança pronto).
  const loadCompany = useCallback((silent = false) => {
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    GetAdminCompanyAPI(id, (response) => {
      if (!response || response.success === false) {
        if (silent) return;
        setError(response?.error || 'Empresa não encontrada.');
        setIsLoading(false);
        return;
      }
      setCompany(response.company);
      setUsers(response.users || []);
      setTutorials(response.tutorials || []);
      setOnboardingEmails(response.onboarding_emails || []);
      setReferral(response.referral || null);
      setIsLoading(false);
    });
  }, [id]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  const applyTest = (nextIsTest, onDone) => {
    SetAdminCompanyTestAPI(company.id, nextIsTest, (response) => {
      if (onDone) onDone();
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao atualizar a empresa. Tente novamente.', 'error');
        return;
      }
      showToast(nextIsTest ? 'Empresa marcada como teste.' : 'Empresa desmarcada como teste.', 'success');
      loadCompany();
    });
  };

  // Marcar como teste libera checkout gratuito no Stripe: sempre confirma antes.
  const handleToggleTest = () => {
    if (!company) return;
    const nextIsTest = !(Number(company.is_test) === 1);
    if (nextIsTest) {
      setMarkTestDialogOpen(true);
      return;
    }
    applyTest(false);
  };

  const handleMarkTestConfirm = () => {
    if (!company || markingTest) return;
    setMarkingTest(true);
    applyTest(true, () => {
      setMarkingTest(false);
      setMarkTestDialogOpen(false);
    });
  };

  const handleStartClosureConfirm = () => {
    if (!company || startingClosure) return;
    setStartingClosure(true);
    StartAdminCompanyClosureAPI(company.id, (response) => {
      setStartingClosure(false);
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao iniciar o encerramento. Tente novamente.', 'error');
        return;
      }
      setStartClosureOpen(false);
      showToast(
        response.notified === false
          ? 'Encerramento iniciado. O e-mail aos responsáveis não foi enviado.'
          : 'Encerramento iniciado. Os responsáveis foram avisados por e-mail.',
        response.notified === false ? 'warning' : 'success'
      );
      loadCompany(true);
    });
  };

  const handleReopenTrialConfirm = () => {
    if (!company || reopeningTrial) return;
    setReopeningTrial(true);
    ReopenAdminCompanyTrialAPI(company.id, (response) => {
      setReopeningTrial(false);
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao liberar o novo período de teste. Tente novamente.', 'error');
        return;
      }
      setReopenTrialOpen(false);
      showToast(`Novo período de teste liberado até ${fmtDate(response.trial_ends_at)}.`, 'success');
      loadCompany(true);
    });
  };

  const handleDeleted = () => {
    setDeleteDialogOpen(false);
    navigate('/admin/companies', { replace: true, state: { deletedCompanyName: company?.name } });
  };

  // deliveries: [{ channel, to_label }], um por destinatário entregue (e-mail, WhatsApp ou os dois).
  const handleMessageSent = (deliveries) => {
    setMessageDialogOpen(false);
    showToast(`Mensagem enviada ${describeDeliveries(deliveries)}.`, 'success');
  };

  if (isLoading) {
    return (
      <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
        <Loading />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
        <Link component={RouterLink} to="/admin/companies" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, fontSize: '0.875rem' }}>
          <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />
          Voltar para empresas
        </Link>
        <Alert severity="error">{error || 'Empresa não encontrada.'}</Alert>
      </Box>
    );
  }

  const { counts = {}, subscription, whatsapp } = company;
  const isTest = Number(company.is_test) === 1;
  const closureState = company.closure?.state || null;
  const canOpenDelete = isTest || closureState === 'expired';
  const canMarkTest = canMarkCompanyTest(company?.id, isTest);
  const canSendMessage = hasCompanyMessageContacts(company);
  const reopenTrialBlocked = reopenTrialBlockedReason(company);

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Link component={RouterLink} to="/admin/companies" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, fontSize: '0.875rem' }}>
        <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />
        Voltar para empresas
      </Link>

      {/* Cabeçalho */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '1.375rem', md: '1.625rem' }, color: 'text.primary' }}>
              {company.name}
            </Typography>
            <CompanyStatusBadge status={company.status} />
            {isTest && <CompanyTestChip />}
            <CompanyClosureChip state={closureState} scheduledAt={company.closure?.scheduled_at} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Empresa #{company.id} · Criada em {fmtDate(company.created_at)}
            {company.email ? ` · ${company.email}` : ''}
            {company.phone ? ` · ${formatPhone(company.phone)}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title={canSendMessage ? '' : NO_CONTACTS_HINT}>
            <span>
              <Button
                variant="outlined"
                startIcon={<SendOutlinedIcon />}
                onClick={() => setMessageDialogOpen(true)}
                disabled={!canSendMessage}
              >
                Enviar mensagem
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canMarkTest ? MARK_TEST_HINT : COMPANY_ZERO_TEST_HINT}>
            <span>
              <Button
                variant="outlined"
                startIcon={<ScienceOutlinedIcon />}
                onClick={handleToggleTest}
                disabled={!canMarkTest}
              >
                {isTest ? 'Desmarcar teste' : 'Marcar como teste'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={reopenTrialBlocked || `Mais ${REOPEN_TRIAL_DAYS} dias de teste a partir de hoje, para chamar a conta de volta.`}>
            <span>
              <Button
                variant="outlined"
                startIcon={<EventRepeatOutlinedIcon />}
                onClick={() => setReopenTrialOpen(true)}
                disabled={!!reopenTrialBlocked}
              >
                Liberar novo período de teste
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canOpenDelete ? '' : DELETE_LOCKED_HINT}>
            <span>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineOutlinedIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={!canOpenDelete}
              >
                Excluir empresa e todos os dados
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Cards de informação */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <CompanyInfoCard
          icon={<BarChartOutlinedIcon fontSize="small" />}
          title="Uso"
          rows={[
            { label: 'Pacientes', value: counts.patients ?? 0 },
            { label: 'Agendamentos', value: counts.appointments ?? 0 },
            { label: 'Futuros', value: counts.appointments_future ?? 0 },
            { label: 'Realizados', value: counts.appointments_completed ?? 0 },
            { label: 'Último login', value: fmtDate(company.last_login_at) },
            { label: 'Última atividade', value: fmtDate(company.last_activity_at) },
          ]}
        />

        <CompanyInfoCard
          icon={<PaymentOutlinedIcon fontSize="small" />}
          title="Assinatura"
          rows={
            subscription
              ? [
                  { label: 'Status', value: SUBSCRIPTION_STATUS_LABEL[subscription.status] || subscription.status || '—' },
                  { label: 'Plano', value: subscription.plan_name || '—' },
                  { label: 'Próxima cobrança', value: fmtDate(subscription.next_due_date) },
                  { label: 'Em trial até', value: fmtDate(company.trial_ends_at) },
                ]
              : [
                  { label: 'Status', value: 'Sem assinatura' },
                  { label: 'Em trial até', value: fmtDate(company.trial_ends_at) },
                ]
          }
        />

        <CompanyInfoCard
          icon={<WhatsAppIcon fontSize="small" />}
          title="WhatsApp"
          rows={
            whatsapp
              ? [
                  { label: 'Número próprio', value: whatsapp.use_own_number ? 'Sim' : 'Não (compartilhado)' },
                  { label: 'Status', value: WHATSAPP_STATUS_LABEL[whatsapp.last_status] || whatsapp.last_status || '—' },
                ]
              : [{ label: 'Status', value: 'Não configurado' }]
          }
        />

        <CompanyInfoCard
          icon={<ReceiptLongOutlinedIcon fontSize="small" />}
          title="Notas fiscais"
          rows={[{ label: 'Emitidas', value: counts.fiscal_invoices ?? 0 }]}
        />

        <CompanySignupOriginCard attribution={company.signup_attribution} />

        <CompanyOutreachOutcomeCard company={company} />

        <CompanyReferralCard
          companyId={company.id}
          referral={referral}
          onToast={showToast}
          onChanged={() => loadCompany(true)}
        />

        <CompanyDemoDataCard
          companyId={company.id}
          demoData={company.demo_data}
          onToast={showToast}
          onChanged={() => loadCompany(true)}
        />
      </Box>

      <CompanySetupCard setup={company.setup} />

      <CompanyOnboardingCard emails={onboardingEmails} />

      <CompanyTutorialsCard tutorials={tutorials} users={users} />

      <CompanyClosureCard
        company={company}
        onStart={() => setStartClosureOpen(true)}
        onArchiveReady={() => loadCompany(true)}
      />

      {/* Usuários */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
        Usuários ({users.length})
      </Typography>
      <CompanyUsersTable users={users} />

      <SendCompanyMessageDialog
        isOpen={messageDialogOpen}
        company={company}
        onCancel={() => setMessageDialogOpen(false)}
        onSent={handleMessageSent}
      />

      <MarkCompanyTestDialog
        isOpen={markTestDialogOpen}
        company={company}
        loading={markingTest}
        onConfirm={handleMarkTestConfirm}
        onCancel={() => setMarkTestDialogOpen(false)}
      />

      <ReopenCompanyTrialDialog
        isOpen={reopenTrialOpen}
        company={company}
        loading={reopeningTrial}
        onConfirm={handleReopenTrialConfirm}
        onCancel={() => setReopenTrialOpen(false)}
      />

      <StartCompanyClosureDialog
        isOpen={startClosureOpen}
        company={company}
        loading={startingClosure}
        onConfirm={handleStartClosureConfirm}
        onCancel={() => setStartClosureOpen(false)}
      />

      <DeleteCompanyDialog
        isOpen={deleteDialogOpen}
        company={company}
        onCancel={() => setDeleteDialogOpen(false)}
        onDeleted={handleDeleted}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminCompanyDetails;
